import { useMemo, useCallback } from "react";
import { analytics } from "../../lib/analytics";
import type { SponsorTierGroup } from "@wondermaps/db";

/** Filter tier groups by placement flag and enforce per-tier caps */
function filterByPlacement(
  groups: SponsorTierGroup[],
  placementFlag: "showInSplash" | "showInWelcome" | "showInHamburger",
  capField?: "maxInSplash" | "maxInWelcome",
): SponsorTierGroup[] {
  return groups
    .filter((g) => g[placementFlag])
    .map((g) => ({
      ...g,
      sponsors: capField ? g.sponsors.slice(0, g[capField]) : g.sponsors,
    }))
    .filter((g) => g.sponsors.length > 0);
}

export function useVisitorSponsors(
  enabled: boolean,
  sponsors: SponsorTierGroup[] | undefined,
) {
  const mapSponsors = useMemo(() => {
    if (!enabled || !sponsors) return undefined;
    if (sponsors.length === 0) return undefined;
    const hasAny = sponsors.some((t) => t.sponsors.length > 0);
    if (!hasAny) return undefined;
    return sponsors;
  }, [enabled, sponsors]);

  /** Sponsors filtered for splash screen (showInSplash + maxInSplash cap) */
  const splashSponsors = useMemo(() => {
    if (!mapSponsors) return undefined;
    const filtered = filterByPlacement(mapSponsors, "showInSplash", "maxInSplash");
    return filtered.length > 0 ? filtered : undefined;
  }, [mapSponsors]);

  /** Sponsors filtered for welcome modal (showInWelcome + maxInWelcome cap) */
  const welcomeSponsors = useMemo(() => {
    if (!mapSponsors) return undefined;
    const filtered = filterByPlacement(mapSponsors, "showInWelcome", "maxInWelcome");
    return filtered.length > 0 ? filtered : undefined;
  }, [mapSponsors]);

  /** Sponsors filtered for hamburger menu (showInHamburger, no cap) */
  const menuSponsors = useMemo(() => {
    if (!mapSponsors) return undefined;
    const filtered = filterByPlacement(mapSponsors, "showInHamburger");
    return filtered.length > 0 ? filtered : undefined;
  }, [mapSponsors]);

  const handleSponsorImpression = useCallback(
    (items: Array<{ id: string; name: string }>, placement: string) => {
      for (const s of items) {
        analytics.sponsorImpression({ sponsor_id: s.id, sponsor_name: s.name, placement });
      }
    },
    []
  );

  const handleSponsorClick = useCallback(
    (sponsor: { id: string; name: string; websiteUrl: string | null }, placement: string) => {
      analytics.sponsorClick({
        sponsor_id: sponsor.id,
        sponsor_name: sponsor.name,
        placement,
        website_url: sponsor.websiteUrl ?? undefined,
      });
    },
    []
  );

  return {
    splashSponsors,
    welcomeSponsors,
    menuSponsors,
    handleSponsorImpression,
    handleSponsorClick,
  };
}
